import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import BookSearchPreview from './BookSearchPreview'
import { useBookSearch, BookSearchResult } from '../hooks/useBookSearch'
import Modal from './Modal'

interface AddPastBookProps {
  clubId: number
  onClose: () => void
  onSuccess: () => void
}

export default function AddPastBook({ clubId, onClose, onSuccess }: AddPastBookProps) {
  const { searchQuery, setSearchQuery, results, loading, error: searchError } = useBookSearch()
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState('')
  const [manualEntry, setManualEntry] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [previewBook, setPreviewBook] = useState<BookSearchResult | null>(null)

  // Form state
  const [bookData, setBookData] = useState({
    title: '',
    author: '',
    startedAt: '',
    completedAt: '',
    unclaimed: true
  })
  const [selectedBook, setSelectedBook] = useState<BookSearchResult | null>(null)

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [results])

  useEffect(() => {
    if (searchError) {
      setError(searchError)
    }
  }, [searchError])

  const handleAddBook = async (book: BookSearchResult, dates: { startedAt: string; completedAt: string; unclaimed: boolean }) => {
    setAdding(true)
    setError('')

    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/book_picks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_pick: {
            status: 'completed',
            started_at: dates.startedAt,
            completed_at: dates.completedAt,
            is_historical: true,
            unclaimed: dates.unclaimed,
            book_attributes: {
              title: book.title,
              author: book.author,
              isbn: book.isbn,
              description: book.description,
              cover_url: book.cover_url,
              first_publish_year: book.first_publish_year,
              number_of_pages: book.number_of_pages,
              metadata: book.metadata
            }
          }
        })
      })

      if (response.ok) {
        onSuccess()
        onClose()
      } else {
        const data = await response.json()
        setError(data.errors?.join(', ') || 'Failed to add book')
      }
    } catch (err) {
      setError('Failed to add book')
    } finally {
      setAdding(false)
    }
  }

  const handleSelectBook = (book: BookSearchResult) => {
    setSelectedBook(book) // Store the full book object with cover_url and metadata
    setBookData({
      ...bookData,
      title: book.title,
      author: book.author
    })
    setManualEntry(true)
    setSearchQuery('')
  }

  const handleSubmit = async () => {
    if (!bookData.title || !bookData.author) {
      setError('Title and author are required')
      return
    }

    if (!bookData.completedAt) {
      setError('Completion date is required')
      return
    }

    // If a book was selected from search, use all its data (cover_url, isbn, etc.)
    // Otherwise, just use the manually entered title and author
    const bookToAdd = selectedBook ? {
      ...selectedBook,
      title: bookData.title, // Allow manual edits to override
      author: bookData.author
    } : {
      title: bookData.title,
      author: bookData.author
    }

    await handleAddBook(
      bookToAdd,
      {
        startedAt: bookData.startedAt,
        completedAt: bookData.completedAt,
        unclaimed: bookData.unclaimed
      }
    )
  }

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="lg" showCloseButton={true}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Add Past Book</h2>
            <p className="text-gray-600">Add a book your club has already read</p>
          </div>
        </div>

        {/* Toggle */}
        <div className="px-6 pt-4">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setManualEntry(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                !manualEntry
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Search Books
            </button>
            <button
              onClick={() => setManualEntry(true)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                manualEntry
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Enter Details
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-2">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {!manualEntry ? (
            <>
              {/* Search Input */}
              <div className="mb-6">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (results.length === 0) return
                    if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      setHighlightedIndex((prev) =>
                        prev < results.length - 1 ? prev + 1 : prev
                      )
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault()
                      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1))
                    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
                      e.preventDefault()
                      handleSelectBook(results[highlightedIndex])
                    }
                  }}
                  placeholder="Search by title or author..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  autoFocus
                />
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              )}

              {/* Search Results */}
              {!loading && results.length > 0 && (
                <div className="space-y-3">
                  {results.map((book, index) => (
                    <button
                      key={index}
                      onClick={() => setPreviewBook(book)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      disabled={adding}
                      className={`w-full bg-white border-2 rounded-xl p-4 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed ${
                        highlightedIndex === index
                          ? 'border-indigo-500 shadow-md bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-400 hover:shadow-md'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row gap-4">
                        {book.cover_url ? (
                          <img
                            src={book.cover_url}
                            alt={book.title}
                            className="w-16 h-24 object-cover rounded shadow-sm"
                          />
                        ) : (
                          <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 mb-1">{book.title}</h3>
                          <p className="text-sm text-gray-600 mb-2">by {book.author}</p>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                            {book.first_publish_year && <span>{book.first_publish_year}</span>}
                            {book.number_of_pages && (
                              <>
                                {book.first_publish_year && <span>•</span>}
                                <span>{book.number_of_pages} pages</span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-indigo-600 mt-1">Click to see details</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!loading && searchQuery.length >= 2 && results.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-600 mb-4">No books found. Try entering details manually.</p>
                  <button
                    onClick={() => setManualEntry(true)}
                    className="text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Enter book manually →
                  </button>
                </div>
              )}

              {!loading && searchQuery.length < 2 && (
                <div className="text-center py-12">
                  <p className="text-gray-600">Start typing to search for books</p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Manual Entry Form */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Book Title *
                  </label>
                  <input
                    id="title"
                    type="text"
                    value={bookData.title}
                    onChange={(e) => setBookData({ ...bookData, title: e.target.value })}
                    placeholder="Enter book title"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-2">
                    Author *
                  </label>
                  <input
                    id="author"
                    type="text"
                    value={bookData.author}
                    onChange={(e) => setBookData({ ...bookData, author: e.target.value })}
                    placeholder="Enter author name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="startedAt" className="block text-sm font-medium text-gray-700 mb-2">
                      Started Reading (Optional)
                    </label>
                    <input
                      id="startedAt"
                      type="date"
                      value={bookData.startedAt}
                      onChange={(e) => setBookData({ ...bookData, startedAt: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    />
                  </div>

                  <div>
                    <label htmlFor="completedAt" className="block text-sm font-medium text-gray-700 mb-2">
                      Completed Reading *
                    </label>
                    <input
                      id="completedAt"
                      type="date"
                      value={bookData.completedAt}
                      onChange={(e) => setBookData({ ...bookData, completedAt: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <input
                    id="unclaimed"
                    type="checkbox"
                    checked={bookData.unclaimed}
                    onChange={(e) => setBookData({ ...bookData, unclaimed: e.target.checked })}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="unclaimed" className="text-sm text-gray-700">
                    Mark as unclaimed (you can claim it later if it was your pick)
                  </label>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={adding || !bookData.title || !bookData.author || !bookData.completedAt}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
                >
                  {adding ? 'Adding book...' : 'Add Past Book'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Book Preview Modal */}
      {previewBook && (
        <BookSearchPreview
          book={previewBook}
          onClose={() => setPreviewBook(null)}
          onSelect={() => {
            handleSelectBook(previewBook)
            setPreviewBook(null)
          }}
          showSelectButton={true}
        />
      )}
    </>
  )
}
